import React, { useMemo } from 'react';
import { Form } from 'react-final-form';
import { styled } from '@linaria/react';
import PropTypes from 'prop-types';

import OAuth2 from '@blocks/_common/oauth2';
import { ProviderButton, ProviderButtonText } from '@blocks/_common/oauth2/oauth2.ui';
import { onOAuth2LoginHandler, onOAuth2OrderHandler } from '@blocks/_common/oauth2/utils';
import { useSessionStore } from '@blocks/stores/session';
import withErrorBoundary from '@blocks/utils/withErrorBoundary';

import GoogleIcon from '../_common/oauth2/google.inline.svg';

import '@blocks/styles/theme.css';
import '@blocks/styles/global.css';
import { togglePolicy } from '@blocks/_common/mutators/order';

const initialValues = {
  stay_signed_in: true,
  policy_agree: false,
  marketing_agree: false,
};

export type SocialLoginProps = {
  domain?: string;
  policyModalTitle?: string;
  policyModalSubmit?: string;
};

const SocialLoginContainer = styled.div`
  display: inline-block;

  & .bb-providerButton {
    width: fit-content;
  }
`;

const Placeholder = () => (
  <SocialLoginContainer>
    <ProviderButton
      type="button"
      isUsed={false}
      isSmall={false}
    >
      <GoogleIcon />
      <ProviderButtonText>Continue with Google</ProviderButtonText>
    </ProviderButton>
  </SocialLoginContainer>
);

const SocialLogin: React.FC<SocialLoginProps> = ({
  domain = '/',
  policyModalSubmit = 'Continue',
  policyModalTitle = 'Review our Terms to sign up',
}) => {
  const {
    isFetching,
    session: { user_role },
  } = useSessionStore(domain);

  const showPlaceholder = useMemo(() => isFetching && !user_role, [isFetching, user_role]);

  if (showPlaceholder) {
    return <Placeholder />;
  }

  if (!user_role) {
    return (
      <Form
        initialValues={{ ...initialValues }}
        onSubmit={onOAuth2LoginHandler}
        subscription={{
          submitting: true,
          values: false,
          validating: true,
          submitSucceeded: true,
          submitFailed: true,
          submitError: true,
          hasSubmitErrors: true,
          dirtySinceLastSubmit: true,
        }}
        mutators={{
          togglePolicy,
        }}
        render={() => (
          <SocialLoginContainer>
            <OAuth2
              domain={domain}
              source_context="authorization"
              policyModalTitle={policyModalTitle}
              policyModalSubmit={policyModalSubmit}
              handler={onOAuth2OrderHandler}
              providers={[
                {
                  provider: 'google',
                  text: 'Google',
                  tooltipText: 'Use Google to Sign up',
                },
              ]}
              isPolicyRequired
              hideDivider
              disableUsedProvider
              placeholder={<Placeholder />}
            />
          </SocialLoginContainer>
        )}
      />
    );
  }

  return null;
};

SocialLogin.propTypes = {
  domain: PropTypes.string,
  policyModalSubmit: PropTypes.string,
  policyModalTitle: PropTypes.string,
};

export default withErrorBoundary(SocialLogin);
